/* =========================================================
   WILDTON — ENTRY INTRO ONLY
   - Plays once per browser tab/session.
   - Does NOT intercept links.
   - Does NOT run on internal page navigation after first entry.
========================================================= */
(() => {
  'use strict';

  const STORAGE_KEY = 'wt_intro_seen_v2';

  const MIN_DURATION = 3200;
  const MAX_DURATION = 6000;
  const END_HOLD = 350;
  const EXIT_DURATION = 650;

  const html = document.documentElement;
  const loader = document.querySelector('[data-wt-loader]');

  const safeGet = (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch (_) {
      return null;
    }
  };

  const safeSet = (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch (_) {}
  };

  const clearLoaderClasses = () => {
    html.classList.remove('wt-loader-prepaint', 'wt-loader-lock');

    if (!loader) return;

    loader.classList.remove(
      'is-visible',
      'is-running',
      'is-exiting'
    );
  };

  if (!loader) {
    html.classList.remove('wt-loader-prepaint', 'wt-loader-lock');
    return;
  }

  /*
    If the intro already played in this browser tab/session,
    remove the loader immediately.
    No click interception and no page-transition animation.
  */
  if (safeGet(STORAGE_KEY) === '1') {
    clearLoaderClasses();
    loader.remove();
    return;
  }

  /*
    Mark this session immediately.
    This ensures that navigating to another Shopify page while the same
    tab/session remains open never triggers this intro again.
  */
  safeSet(STORAGE_KEY, '1');

  const topProgress = loader.querySelector('[data-wt-progress-top]');
  const bottomProgress = loader.querySelector('[data-wt-progress-bottom]');

  const setProgress = (value) => {
    const n = Math.max(0, Math.min(100, Math.round(value)));
    const label = `${n}%`;

    if (topProgress) topProgress.textContent = label;
    if (bottomProgress) bottomProgress.textContent = label;
  };

  const showLoader = () => {
    loader.classList.remove('is-exiting');
    loader.classList.add('is-visible', 'is-running');

    html.classList.add('wt-loader-lock');
    html.classList.remove('wt-loader-prepaint');

    setProgress(0);
  };

  const hideLoader = () => {
    loader.classList.add('is-exiting');

    window.setTimeout(() => {
      clearLoaderClasses();
      loader.remove();
    }, EXIT_DURATION);
  };

  const pageLoaded = () => document.readyState === 'complete';

  showLoader();

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reducedMotion) {
    setProgress(100);

    if (pageLoaded()) {
      window.setTimeout(hideLoader, 40);
    } else {
      window.addEventListener(
        'load',
        () => window.setTimeout(hideLoader, 40),
        { once: true }
      );
    }

    return;
  }

  let loaded = pageLoaded();

  if (!loaded) {
    window.addEventListener(
      'load',
      () => {
        loaded = true;
      },
      { once: true }
    );
  }

  const startedAt = performance.now();
  let displayed = 0;
  let completed = false;

  const tick = (now) => {
    if (completed) return;

    const elapsed = now - startedAt;

    /*
      0–90%: visual loading progress.
      90–100%: completes once the page is loaded.
      MAX_DURATION is a safety fallback so third-party scripts can never
      trap the storefront behind the intro.
    */
    const baseT = Math.min(elapsed / MIN_DURATION, 1);
    const eased = 1 - Math.pow(1 - baseT, 2.6);

    let target = eased * 90;

    if (loaded && elapsed >= MIN_DURATION * 0.76) {
      const finishT = Math.min(
        (elapsed - MIN_DURATION * 0.76) / 900,
        1
      );

      target = Math.max(target, 90 + finishT * 10);
    }

    if (elapsed >= MAX_DURATION) {
      target = 100;
    }

    displayed += (target - displayed) * 0.14;

    if (target >= 100 && displayed >= 99.2) {
      displayed = 100;
      setProgress(100);
      completed = true;

      window.setTimeout(hideLoader, END_HOLD);
      return;
    }

    setProgress(displayed);
    requestAnimationFrame(tick);
  };

  requestAnimationFrame(tick);

  /*
    Browser back-forward cache safety.
    The loader must never remain over a restored page.
  */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    clearLoaderClasses();

    if (loader && loader.isConnected) {
      loader.remove();
    }
  });
})();